// src/components/MCQSetter.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  getOptionLetters,
  serializeMCQ,
  parseMCQ,
} from "../utils/mcqParser";
import {
  ArrowPathIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  ClipboardIcon,
  TrashIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";

const STORAGE_KEY = "setter_mcq_state";

const getSavedMCQState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (err) {
    console.error("Failed to load saved MCQ state:", err);
  }
  return null;
};

const MCQSetter = ({ selectedTopic }) => {
  const savedState = useMemo(() => getSavedMCQState(), []);

  const [fileName, setFileName] = useState(() => savedState?.fileName || "");
  const [offset, setOffset] = useState(() =>
    typeof savedState?.offset === "number" ? savedState.offset : 0
  );
  const [numQuestions, setNumQuestions] = useState(
    () => savedState?.numQuestions || 10
  );
  const [numOptions, setNumOptions] = useState(
    () => savedState?.numOptions || 4
  );
  const [questions, setQuestions] = useState(() => {
    if (Array.isArray(savedState?.questions) && savedState.questions.length > 0) {
      return savedState.questions;
    }
    return Array.from({ length: 10 }, () => ({ options: [], note: "" }));
  });

  const [existingFiles, setExistingFiles] = useState([]);
  const [selectedFileToLoad, setSelectedFileToLoad] = useState("");
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null); // { type: 'success' | 'error' | 'info', text: string }
  const [copySuccess, setCopySuccess] = useState(false);

  const backendURL = process.env.REACT_APP_BACKEND_URL;

  // Persist draft MCQ state in localStorage
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          fileName,
          offset,
          numQuestions,
          numOptions,
          questions,
        })
      );
    } catch (err) {
      console.error("Failed to save MCQ state to localStorage:", err);
    }
  }, [fileName, offset, numQuestions, numOptions, questions]);

  // Option letters based on current numOptions count (e.g. ['A', 'B', 'C', 'D'])
  const optionLetters = useMemo(() => getOptionLetters(numOptions), [numOptions]);

  // Fetch list of files for active topic
  const fetchTopicFiles = useCallback(async () => {
    if (!selectedTopic) return;
    try {
      setLoadingFiles(true);
      const res = await axios.post(`${backendURL}/list_files`, {
        topic: selectedTopic,
      });
      if (res.status === 200 && Array.isArray(res.data.files)) {
        setExistingFiles(res.data.files);
      }
    } catch (err) {
      console.error("Error fetching files list:", err);
    } finally {
      setLoadingFiles(false);
    }
  }, [backendURL, selectedTopic]);

  useEffect(() => {
    fetchTopicFiles();
  }, [fetchTopicFiles]);

  // Dynamic adjustments when numQuestions changes
  const handleNumQuestionsChange = (value) => {
    const count = Math.max(1, Math.min(500, parseInt(value, 10) || 1));
    setNumQuestions(count);
    setQuestions((prev) => {
      if (count === prev.length) return prev;
      if (count > prev.length) {
        const additional = Array.from({ length: count - prev.length }, () => ({
          options: [],
          note: "",
        }));
        return [...prev, ...additional];
      }
      return prev.slice(0, count);
    });
  };

  // Toggle answer option for a specific question index
  const toggleOption = (qIndex, letter) => {
    setQuestions((prev) => {
      const next = [...prev];
      const currentOpts = next[qIndex]?.options || [];
      const isSelected = currentOpts.includes(letter);

      const newOpts = isSelected
        ? currentOpts.filter((opt) => opt !== letter)
        : [...currentOpts, letter].sort((a, b) => a.localeCompare(b));

      next[qIndex] = {
        ...next[qIndex],
        options: newOpts,
      };
      return next;
    });
  };

  // Update note text for a question
  const handleNoteChange = (qIndex, note) => {
    setQuestions((prev) => {
      const next = [...prev];
      next[qIndex] = {
        ...next[qIndex],
        note,
      };
      return next;
    });
  };

  // Clear selections for a single question
  const clearQuestion = (qIndex) => {
    setQuestions((prev) => {
      const next = [...prev];
      next[qIndex] = { options: [], note: "" };
      return next;
    });
  };

  // Clear all questions
  const clearAllQuestions = () => {
    if (window.confirm("Clear all questions, answers, and notes?")) {
      setQuestions(
        Array.from({ length: numQuestions }, () => ({ options: [], note: "" }))
      );
      setStatusMessage({ type: "info", text: "All questions cleared." });
    }
  };

  // Compile serialized MCQ text
  const serializedOutput = useMemo(() => {
    const mapped = questions.map((q, idx) => ({
      number: offset + idx + 1,
      options: q.options,
      note: q.note,
    }));
    return serializeMCQ(mapped);
  }, [questions, offset]);

  // Load and strictly parse an existing file from server
  const loadExistingFile = async (filenameToLoad) => {
    if (!filenameToLoad) return;
    try {
      setLoadingContent(true);
      setStatusMessage({
        type: "info",
        text: `Loading "${filenameToLoad}"...`,
      });

      const res = await axios.post(`${backendURL}/get_code`, {
        filename: filenameToLoad,
        topic: selectedTopic,
      });

      if (res.status === 200 && res.data?.code !== undefined) {
        const parseResult = parseMCQ(res.data.code);

        if (!parseResult.success) {
          setStatusMessage({
            type: "error",
            text: `Parse error: ${parseResult.error}`,
          });
          return;
        }

        const {
          offset: parsedOffset,
          numQuestions: parsedCount,
          numOptions: parsedOptions,
          questions: parsedQList,
        } = parseResult.data;

        setFileName(filenameToLoad);
        setOffset(parsedOffset);
        setNumQuestions(parsedCount);
        setNumOptions(parsedOptions);

        const mapByNum = new Map();
        parsedQList.forEach((q) => mapByNum.set(q.number, q));

        const newQArray = [];
        for (let i = 0; i < parsedCount; i++) {
          const expectedNumber = parsedOffset + i + 1;
          const found = mapByNum.get(expectedNumber);
          newQArray.push({
            options: found ? found.options : [],
            note: found ? found.note : "",
          });
        }

        setQuestions(newQArray);
        setStatusMessage({
          type: "success",
          text: `Loaded "${filenameToLoad}" (Offset: ${parsedOffset}, Qs: ${parsedCount}, Options: ${parsedOptions})`,
        });
      } else {
        setStatusMessage({
          type: "error",
          text: "Server did not return file content.",
        });
      }
    } catch (err) {
      console.error(err);
      setStatusMessage({
        type: "error",
        text: `Load failed: ${err.message || err}`,
      });
    } finally {
      setLoadingContent(false);
    }
  };

  // Save the MCQ file to the server
  const handleSave = async () => {
    const trimmedFilename = fileName.trim();
    if (!trimmedFilename) {
      setStatusMessage({
        type: "error",
        text: "Please enter a File Name.",
      });
      return;
    }

    if (!selectedTopic) {
      setStatusMessage({
        type: "error",
        text: "Select a topic first.",
      });
      return;
    }

    try {
      setIsSaving(true);
      setStatusMessage({ type: "info", text: "Saving file..." });

      const res = await axios.post(`${backendURL}/save_code`, {
        filename: trimmedFilename,
        code: serializedOutput,
        topic: selectedTopic,
      });

      if (res.status === 200) {
        setStatusMessage({
          type: "success",
          text: `Saved "${trimmedFilename}" successfully!`,
        });

        // Notify FilesTab and update sessionStorage cache
        try {
          const cacheKey = `codetyper_files_cache_${selectedTopic}`;
          const raw = sessionStorage.getItem(cacheKey);
          const parsed = raw ? JSON.parse(raw) : {};
          parsed[trimmedFilename] = serializedOutput;
          sessionStorage.setItem(cacheKey, JSON.stringify(parsed));
        } catch (e) {}

        window.dispatchEvent(
          new CustomEvent("codetyper:file_saved", {
            detail: {
              topic: selectedTopic,
              filename: trimmedFilename,
              code: serializedOutput,
            },
          })
        );

        fetchTopicFiles();
      } else {
        setStatusMessage({
          type: "error",
          text: "Failed to save file.",
        });
      }
    } catch (err) {
      console.error("Save error:", err);
      setStatusMessage({
        type: "error",
        text: `Save failed: ${err.message || err}`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Copy preview to clipboard
  const handleCopyPreview = () => {
    navigator.clipboard.writeText(serializedOutput).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // Answered count
  const answeredCount = questions.filter(
    (q) => q.options.length > 0 || q.note.trim().length > 0
  ).length;

  return (
    <div className="w-full space-y-3">
      {/* ================= COMPACT STICKY TOP DASHBOARD ================= */}
      <div className="sticky top-16 z-30 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md shadow-md rounded-xl p-3 border border-gray-200 dark:border-gray-700 space-y-2.5">
        {/* ROW 1: Topic, File loader, File name, Save and Tool Actions */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* File Picker & Name Input */}
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-[280px]">
            {/* Topic Badge */}
            <span className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 whitespace-nowrap">
              {selectedTopic || "No Topic"}
            </span>

            {/* Load Existing Dropdown */}
            <div className="flex items-center gap-1">
              <select
                value={selectedFileToLoad}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedFileToLoad(val);
                  if (val) loadExistingFile(val);
                }}
                disabled={loadingFiles || loadingContent}
                className="text-xs px-2.5 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 max-w-[160px] sm:max-w-[200px] truncate"
              >
                <option value="">
                  {loadingFiles ? "Loading..." : "📂 Load existing file..."}
                </option>
                {existingFiles.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={fetchTopicFiles}
                disabled={loadingFiles}
                title="Refresh files"
                className="p-1.5 text-gray-500 hover:text-indigo-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-lg transition"
              >
                <ArrowPathIcon className={`w-3.5 h-3.5 ${loadingFiles ? "animate-spin" : ""}`} />
              </button>
            </div>

            {/* File Name Input */}
            <div className="flex-1 min-w-[140px] flex items-center">
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Filename (e.g. set_1.txt)"
                className="w-full px-2.5 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono"
              />
            </div>
          </div>

          {/* Action Buttons (Clear, Save) */}
          <div className="flex items-center gap-2 ml-auto">
            {/* Clear All */}
            <button
              type="button"
              onClick={clearAllQuestions}
              title="Clear all question inputs"
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition"
            >
              <TrashIcon className="w-4 h-4" />
            </button>

            {/* Save Button */}
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || loadingContent}
              className="px-4 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 rounded-lg shadow-sm flex items-center gap-1.5 transition disabled:opacity-50"
            >
              {isSaving ? (
                <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <CheckIcon className="w-3.5 h-3.5 stroke-[2.5]" />
              )}
              <span>Save File</span>
            </button>
          </div>
        </div>

        {/* ROW 2: Compulsory Settings (Offset, Count, Options) & Stats */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100 dark:border-gray-700/80 text-xs">
          {/* Settings Inputs */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Offset */}
            <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-700/60 px-2 py-1 rounded-md border border-gray-200 dark:border-gray-600">
              <span className="font-semibold text-gray-600 dark:text-gray-300">Offset:</span>
              <input
                type="number"
                min="0"
                value={offset}
                onChange={(e) => setOffset(Math.max(0, parseInt(e.target.value, 10) || 0))}
                className="w-12 px-1 py-0.5 text-center text-xs font-bold bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-indigo-600 dark:text-indigo-400 focus:outline-none"
                title="Offset: Last question already set"
              />
              <span className="text-[11px] text-gray-400 font-mono">→ Q{offset + 1}</span>
            </div>

            {/* Question Count */}
            <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-700/60 px-2 py-1 rounded-md border border-gray-200 dark:border-gray-600">
              <span className="font-semibold text-gray-600 dark:text-gray-300">Total Qs:</span>
              <input
                type="number"
                min="1"
                max="500"
                value={numQuestions}
                onChange={(e) => handleNumQuestionsChange(e.target.value)}
                className="w-12 px-1 py-0.5 text-center text-xs font-bold bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-indigo-600 dark:text-indigo-400 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => handleNumQuestionsChange(numQuestions + 5)}
                className="px-1 text-[10px] font-bold text-gray-500 hover:text-indigo-600 bg-gray-200 dark:bg-gray-600 rounded"
              >
                +5
              </button>
              <button
                type="button"
                onClick={() => handleNumQuestionsChange(numQuestions + 10)}
                className="px-1 text-[10px] font-bold text-gray-500 hover:text-indigo-600 bg-gray-200 dark:bg-gray-600 rounded"
              >
                +10
              </button>
            </div>

            {/* Options Count */}
            <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-700/60 px-2 py-1 rounded-md border border-gray-200 dark:border-gray-600">
              <span className="font-semibold text-gray-600 dark:text-gray-300">Options:</span>
              <input
                type="number"
                min="2"
                max="10"
                value={numOptions}
                onChange={(e) =>
                  setNumOptions(Math.max(2, Math.min(10, parseInt(e.target.value, 10) || 4)))
                }
                className="w-10 px-1 py-0.5 text-center text-xs font-bold bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-indigo-600 dark:text-indigo-400 focus:outline-none"
              />
              <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400">
                ({optionLetters[0]}-{optionLetters[optionLetters.length - 1]})
              </span>
            </div>
          </div>

          {/* Progress / Status indicator */}
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full font-semibold text-[11px] bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
              {answeredCount}/{numQuestions} Answered
            </span>
            <span className="text-[11px] text-gray-400 font-mono hidden sm:inline">
              Range: {offset + 1}..{offset + numQuestions}
            </span>
          </div>
        </div>

        {/* Compact Toast / Status banner */}
        {statusMessage && (
          <div
            className={`p-2 px-3 rounded-lg flex items-center justify-between text-xs transition shadow-xs ${
              statusMessage.type === "success"
                ? "bg-green-100 text-green-800 border border-green-200 dark:bg-green-950/60 dark:text-green-300 dark:border-green-800"
                : statusMessage.type === "error"
                ? "bg-red-100 text-red-800 border border-red-200 dark:bg-red-950/60 dark:text-red-300 dark:border-red-800"
                : "bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800"
            }`}
          >
            <span>{statusMessage.text}</span>
            <button
              onClick={() => setStatusMessage(null)}
              className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 ml-2 font-bold"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* ================= FULL WIDTH SPLIT LAYOUT ================= */}
      {/* Left: Questions (1 question per line) | Right: Live Preview matching height */}
      <div className="flex flex-col lg:flex-row gap-4 items-stretch w-full">
        {/* LEFT COLUMN: ONE QUESTION PER LINE */}
        <div className="flex-1 w-full lg:min-w-0 space-y-2">
          {questions.map((q, idx) => {
            const qNum = offset + idx + 1;
            const hasAnswer = q.options.length > 0;
            const hasNote = Boolean(q.note && q.note.trim());

            return (
              <div
                key={qNum}
                className={`flex items-center gap-2.5 p-2 px-3 rounded-lg border transition-all ${
                  hasAnswer || hasNote
                    ? "bg-white dark:bg-gray-800 border-indigo-300 dark:border-indigo-700/80 shadow-xs ring-1 ring-indigo-200 dark:ring-indigo-900/30"
                    : "bg-white/90 dark:bg-gray-800/80 border-gray-200 dark:border-gray-700"
                }`}
              >
                {/* Question Badge */}
                <span className="w-8 text-xs font-bold font-mono text-indigo-700 dark:text-indigo-400 text-right flex-shrink-0">
                  {qNum})
                </span>

                {/* Option Buttons (A, B, C, D...) */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {optionLetters.map((letter) => {
                    const isSelected = q.options.includes(letter);
                    return (
                      <button
                        key={letter}
                        type="button"
                        onClick={() => toggleOption(idx, letter)}
                        className={`w-8 h-8 rounded-lg font-bold text-xs select-none transition-all flex items-center justify-center ${
                          isSelected
                            ? "bg-indigo-600 text-white shadow-xs scale-105"
                            : "bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {letter}
                      </button>
                    );
                  })}
                </div>

                {/* Note Input */}
                <input
                  type="text"
                  value={q.note}
                  onChange={(e) => handleNoteChange(idx, e.target.value)}
                  placeholder="Notes / explanation (optional)..."
                  className="flex-1 min-w-[100px] px-2.5 py-1.5 text-xs bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-md text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />

                {/* Clear Single Question */}
                {(hasAnswer || hasNote) && (
                  <button
                    type="button"
                    onClick={() => clearQuestion(idx)}
                    title="Clear question"
                    className="text-gray-300 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 text-xs px-1 flex-shrink-0 transition"
                  >
                    ✕
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* RIGHT COLUMN: LIVE PREVIEW MATCHING QUESTIONS HEIGHT */}
        <div className="w-full lg:w-[380px] xl:w-[460px] flex-shrink-0 flex flex-col">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/90 dark:border-gray-700 overflow-hidden flex flex-col h-full flex-1">
            {/* Harmonious Header Adapted to UI */}
            <div className="px-3.5 py-2.5 bg-gray-50/90 dark:bg-gray-700/50 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <DocumentArrowDownIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
                  Live Preview
                </span>
                <span className="px-2 py-0.5 text-[11px] font-mono font-semibold rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60">
                  {numQuestions} lines
                </span>
              </div>

              {/* Copy Button */}
              <button
                type="button"
                onClick={handleCopyPreview}
                className="px-2.5 py-1 text-xs font-medium bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md shadow-2xs transition flex items-center gap-1.5 active:scale-95"
              >
                {copySuccess ? (
                  <>
                    <ClipboardDocumentCheckIcon className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-green-600 dark:text-green-400 font-semibold">Copied!</span>
                  </>
                ) : (
                  <>
                    <ClipboardIcon className="w-3.5 h-3.5 text-gray-400" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>

            {/* Monospace Formatted Output */}
            <div className="p-3.5 bg-gray-900 text-gray-100 font-mono text-xs flex-1 overflow-y-auto whitespace-pre leading-relaxed select-all">
              {serializedOutput || "# No questions generated yet."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCQSetter;
